import * as R from 'ramda';

const CPF_MASK = 'AAA.AAA.AAA-AAA';
const CPF_DISPLAY_MASK = 'XXX.XXX.XXX-XX';
const CPF_MASK_SIGN = '000.000.000-00';
const CNPJ_MASK = 'AA.AAA.AAA/AAAA-AA';
const KNOWN_INVALID_CPF = ['00000000000', '11111111111', '22222222222', '33333333333',
  '44444444444', '55555555555', '66666666666', '77777777777', '88888888888', '99999999999'];
const KNOWN_INVALID_CNPJ = ['00000000000000', '11111111111111', '22222222222222',
  '33333333333333', '44444444444444', '55555555555555', '66666666666666', '77777777777777', '88888888888888', '99999999999999'];

export class CPFCNPJUtil {
  public static validateCNPJ(cnpj: string): boolean {
    if (!cnpj) {
      return false;
    }

    const normalizedCnpj = cnpj.toUpperCase();

    var isKnownInvalid = KNOWN_INVALID_CNPJ.some(x => x === normalizedCnpj);
    if (!isKnownInvalid) isKnownInvalid = this.hasAllEqualCharacters(normalizedCnpj);
    const hasValidFormat = new RegExp('^[0-9A-Z]{12}[0-9]{2}$').test(normalizedCnpj);

    if (isKnownInvalid || !hasValidFormat) {
      return false;
    }

    const hasValidFirstDigit = this.validarPrimeiroDigitoCnpj(normalizedCnpj);
    const hasValidSecondDigit = this.validarSegundoDigitoCnpj(normalizedCnpj);

    return hasValidFirstDigit && hasValidSecondDigit;
  }

  public static validateCPF(cpf) {
    return !(KNOWN_INVALID_CPF.some(x => cpf === x) || !new RegExp('[0-9]{11}').test(cpf) ||
      !this.validarPrimeiroDigitoCpf(cpf) || !this.validarSegundoDigitoCpf(cpf));
  }

  public static validate(value) {
    return this.isCPF(value) ? this.validateCPF(value) : this.validateCNPJ(value);
  }

  public static getMask(value) {
    return this.isCPF(value) ? this.getCpfMask() : this.getCnpjMask();
  }

  public static isCPF(value) {
    return R.pathOr(0, ['length'], value) === 11;
  }

  public static isCNPJ(value) {
    return R.pathOr(0, ['length'], value) === 14;
  }

  public static getPattern() {
    return '([0-9A-Za-z]{2}[\.]?[0-9A-Za-z]{3}[\.]?[0-9A-Za-z]{3}[\/]?[0-9A-Za-z]{4}[-]?[0-9]{2})|([0-9]{3}[\.]?[0-9]{3}[\.]?[0-9]{3}[-]?[0-9]{2})';
  }

  public static getCpfMask() {
    return CPF_MASK;
  }

  public static getCpfMaskSign() {
    return CPF_MASK_SIGN;
  }

  public static getCnpjMask() {
    return CNPJ_MASK;
  }

  public static getCpfDisplayMask() {
    return CPF_DISPLAY_MASK;
  }

  public static formatSsn(cpfCnpj: string): string {
    if (!cpfCnpj) {
      return '-';
    }

    cpfCnpj = cpfCnpj.replace('.', '').replace('.', '').replace('/', '').replace('-', '');

    if (!cpfCnpj) {
      return '-';
    }

    return this.isCPF(cpfCnpj) ? this.formatSsnToCpf(cpfCnpj) : this.formatSsnToCnpj(cpfCnpj);
  }

  public static formatSssnWithSpecifcMask(ssn, isCpf) {
    return isCpf ? CPFCNPJUtil.formatSsnToCpf(ssn) : CPFCNPJUtil.formatSsnToCnpj(ssn);
  }

  public static formatSsnToCpf(cpf: string) {
    return `${cpf.substr(0, 3) + '.'}${cpf.substr(3, 3) + '.'}${cpf.substr(6, 3) + '-'}${cpf.substr(9, 2)}`;
  }


  public static formatSsnToCnpj(cnpj: string): string {
    return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(-2)}`;
  }

  private static validarPrimeiroDigitoCpf(cpf) {
    return this.validarDigitoCpf(cpf, 10);
  }

  private static validarSegundoDigitoCpf(cpf) {
    return this.validarDigitoCpf(cpf, 11);
  }

  private static validarPrimeiroDigitoCnpj(cnpj) {
    const base = cnpj.substr(0, 12);
    const primeiroDigito = this.calcularDigitoCnpj(base);
    return primeiroDigito === Number(cnpj.charAt(12));
  }

  private static validarSegundoDigitoCnpj(cnpj) {
    const base = cnpj.substr(0, 12);
    const primeiroDigito = this.calcularDigitoCnpj(base);
    const segundoDigito = this.calcularDigitoCnpj(`${base}${primeiroDigito}`);
    return segundoDigito === Number(cnpj.charAt(13));
  }

  private static validarDigitoCpf(cpf, pos) {
    let soma = 0;
    for (let i = pos; i > 1; i--) {
      soma += cpf.charAt(pos - i) * i;
    }
    return this.validarDigitoEsperado(soma, cpf.charAt(pos - 1));
  }

  private static calcularDigitoCnpj(base) {
    let soma = 0;
    let peso = 2;

    for (let i = base.length - 1; i >= 0; i--) {
      soma += this.getCnpjCharValue(base.charAt(i)) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }

    const resto = soma % 11;
    return resto === 0 || resto === 1 ? 0 : 11 - resto;
  }

  private static getCnpjCharValue(char) {
    return char.charCodeAt(0) - 48;
  }

  private static validarDigitoEsperado(soma, esperado) {
    const result = (soma % 11) < 2 ? 0 : 11 - (soma % 11);
    return result == esperado;
  }

  public static obfuscateCpfCnpj(cpfCnpj) {
    if (!cpfCnpj.includes('*') && cpfCnpj != '-') {
      cpfCnpj = cpfCnpj.replace('.', '').replace('.', '').replace('/', '').replace('-', '');
      const result = this.isCPF(cpfCnpj) ?
        `${cpfCnpj.substr(0, 3)}******${cpfCnpj.substr(9, 2)}` :
        `${cpfCnpj.substr(0, 2)}******${cpfCnpj.substr(8, 6)}`;
      return this.formatSsn(result);
    }
    return cpfCnpj;
  }

  public static canObfuscateCpfCnpj(cpfCnpj?: string): string {
    if (!cpfCnpj?.trim()) {
      return "-";
    }
    return this.obfuscateCpfCnpj(cpfCnpj);
  }

  private static hasAllEqualCharacters(value: string): boolean {
  return /^([0-9A-Z])\1+$/.test(value);
}

}
